import rocketSvg from './score-og-rocket.svg';
import { useState } from 'react';
import Modal from '@/shared/ui/Modal';
import Button from '@/shared/ui/Button';
import { useWaitlistSignup } from '@/shared/utils/useWaitlistSignup';

const CHOICE_LABELS = {
  gotIt: 'Got it',
  somewhat: 'Somewhat',
  notReally: 'Not really',
};

// The programme runs one market at a time, so the form asks no country and
// tags every applicant with this one; the list segments on it.
const COUNTRY = 'Korea';

// Mailjet contact properties are text and a campaign template reads
// `feedback` verbatim as [[data:feedback]], so the choice travels as its
// bracketed English label with the free-text note after it; the label stays
// the English constant under every language, like the analytics ones.
const CHOICES = [
  { key: 'gotIt', label: 'Got it', emoji: '👍' },
  { key: 'somewhat', label: 'Somewhat', emoji: '😐' },
  { key: 'notReally', label: 'Not really', emoji: '👎' },
] as const;

export default function ScoreOgModal({ onClose }: { onClose: () => void }) {
  const [choice, setChoice] = useState<(typeof CHOICES)[number] | null>(null);
  const [note, setNote] = useState('');
  const feedback = [choice === null ? '' : `[${choice.label}]`, note.trim()]
    .filter((part) => part !== '')
    .join(' ');
  const { email, setEmail, social, setSocial, status, submit, backToForm } =
    useWaitlistSignup('score_og', {
      country: COUNTRY,
      ...(feedback === '' ? {} : { feedback }),
    });

  const done = status === 'success' || status === 'duplicate';

  return (
    <Modal closeModal={onClose} sheet>
      <div
        className={`flex max-w-full flex-col px-3xl pt-2xl pb-3xl max-md:w-full max-md:px-2xl max-md:pb-4xl ${done ? 'w-103 gap-2xl' : 'w-129 gap-l'}`}
      >
        {done ?
          <>
            <img
              src={rocketSvg}
              alt=""
              width={96}
              height={96}
              className="mx-auto size-24 rounded-full bg-background-main"
            />
            <p className="text-h2 font-medium text-gray-text">
              Thanks for trying Score!
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              {status === 'duplicate' ?
                'You’re already on the list.'
              : 'You’re in.'}
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              Your invite is on its way to{' '}
              <span className="font-bold wrap-break-word">{email}</span>
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              Not right?{' '}
              <button
                type="button"
                className="btn-reset inline text-body-lg font-medium text-purple-default hover:text-purple-hover"
                onClick={backToForm}
              >
                Edit details
              </button>
            </p>
            <Button
              variant="primary"
              size="lg"
              className="h-12 w-full"
              onClick={onClose}
            >
              Continue
            </Button>
          </>
        : <>
            <p className="text-h2 font-medium text-gray-text">
              🔥 Become a Score OG!
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              Join as early adopter during closed beta; get boosted profit share
              and other perks.
            </p>
            <p className="text-body-lg font-medium text-gray-text">
              What we ask: predict/back, suggest markets/features, tell us what
              isn’t working.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              className="flex flex-col gap-l"
            >
              <label className="flex flex-col gap-xs">
                <span className="text-body-md font-medium text-gray">
                  Your email (for the invite and details)
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={status === 'loading'}
                  className="h-12 w-full rounded-sm border border-gray-5 bg-transparent px-l text-body-md font-medium text-gray-text placeholder:text-gray-3 focus:outline-none"
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className="text-body-md font-medium text-gray">
                  Your X, Telegram, KakaoTalk etc
                </span>
                <input
                  type="text"
                  value={social}
                  onChange={(e) => setSocial(e.target.value)}
                  disabled={status === 'loading'}
                  className="h-12 w-full rounded-sm border border-gray-5 bg-transparent px-l text-body-md font-medium text-gray-text focus:outline-none"
                />
              </label>
              <fieldset className="flex flex-col gap-s">
                <legend className="mb-s text-body-md font-medium text-gray">
                  Did the model make sense to you?
                </legend>
                <div className="flex gap-m">
                  {CHOICES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      aria-pressed={choice === item}
                      disabled={status === 'loading'}
                      onClick={() => setChoice(item)}
                      className="btn-reset flex h-18 flex-1 flex-col items-center justify-center gap-xs rounded-[10px] bg-background-main px-s py-m text-center transition-colors hover:bg-gray-5 aria-pressed:bg-purple-default aria-pressed:text-white max-md:px-xs"
                    >
                      <span className="text-body-md" aria-hidden>
                        {item.emoji}
                      </span>
                      <span className="text-body-lg font-medium text-inherit max-md:whitespace-nowrap">
                        {CHOICE_LABELS[item.key]}
                      </span>
                    </button>
                  ))}
                </div>
              </fieldset>
              <label className="flex flex-col gap-xs">
                <span className="text-body-md font-medium text-gray">
                  What did not make sense?
                </span>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={status === 'loading'}
                  className="h-12 w-full rounded-sm border border-gray-5 bg-transparent px-l text-body-md font-medium text-gray-text focus:outline-none"
                />
              </label>
              <div className="flex flex-col items-center gap-s">
                <p className="text-center text-body-sm font-medium text-gray">
                  Your data is protected by our Privacy Policy, email never
                  shared.
                </p>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="h-12 w-full"
                  disabled={status === 'loading' || email === ''}
                  loading={status === 'loading'}
                >
                  Submit
                </Button>
              </div>
              {status === 'error' && (
                <p className="text-body-sm text-red-default">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </>
        }
      </div>
    </Modal>
  );
}
